"use client";

// ===================================================
// OverlayLayer — the one way to put a popup above the app
// ===================================================
//
// Every transient surface (dropdown, context menu, block menu, floating
// toolbar) goes through here. Two things happen, and BOTH are needed:
//
//   1. The children are portalled to <body>. This is the part that actually
//      fixes the bugs Gal reported: a portalled node has no ancestor left to
//      clip it with `overflow: hidden`, and none to trap it inside a stacking
//      context opened by `transform` / `filter` / `will-change`. No z-index,
//      however large, can escape either of those from the inside.
//   2. It is stamped with a layer from the one scale in lib/ui/layers.ts, so
//      whoever wins is a decision someone made rather than an accident of
//      which file was written last.
//
// `useAnchorRect` is the other half: a portalled popup has lost its parent,
// so it can no longer be positioned with `absolute` against the trigger.
// It measures the trigger in viewport coordinates and keeps that fresh
// while scrolling and resizing, which is what `position: fixed` needs.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Z, type ZLayer } from "@/lib/ui/layers";

interface OverlayLayerProps {
  children: ReactNode;
  /** Which rung of the one scale. Defaults to the popup rung. */
  layer?: Extract<ZLayer, "popover" | "modal" | "toast" | "tooltip">;
}

export function OverlayLayer({ children, layer = "popover" }: OverlayLayerProps) {
  // Portals need a DOM that exists. Mount-gated so the server render and the
  // first client render agree and React does not report a hydration mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: Z[layer], pointerEvents: "none" }}>
      {/* The wrapper is inert so it never swallows clicks meant for the page;
          each popup inside re-enables pointer events for itself. */}
      <div style={{ pointerEvents: "auto" }}>{children}</div>
    </div>,
    document.body
  );
}

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Viewport rect of the trigger, refreshed on scroll and resize while `open`.
 *
 * `capture: true` on the scroll listener matters: the trigger usually sits
 * inside a scrolling panel, and a scroll inside that panel does not bubble
 * to window. Without capture the popup would stay put while its button
 * slid away underneath it.
 */
export function useAnchorRect(
  ref: RefObject<HTMLElement | null>,
  open: boolean
): AnchorRect | null {
  const [rect, setRect] = useState<AnchorRect | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top,
      bottom: r.bottom,
      left: r.left,
      right: r.right,
      width: r.width,
      height: r.height,
    });
  }, [ref]);

  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, measure]);

  return rect;
}
